import { Select as ChakraSelect, FormControl, InputGroup, InputRightElement, Spinner } from '@chakra-ui/react';
import { CheckIcon } from '@chakra-ui/icons';
import { useState, useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useField, useFormikContext } from 'formik';
import { useUpdateUser } from '@components/crm/shared/queries';
import { InputState } from '../../types';
import InputLabel from './InputLabel';
import { colors } from '../../constants';
import useStandardToast from './useStandardToast';
import { getChanges, handleFieldValue } from '../../utils';

export default function Select({
  userId = null,
  autosave = false,
  onChange = null,
  name,
  label = null,
  loading = false,
  refetchQuery = null, // a query to refetch after saving
  softRequired = false,
  bypassRightElement = false,
  fontSize = 16,
  ...rest
}) {
  const [state, setState] = useState(InputState.IDLE);
  const [field, meta] = useField(name);
  const showToast = useStandardToast();
  const { values, initialValues, setFieldValue } = useFormikContext();
  const queryClient = useQueryClient();

  const changeSet = getChanges(initialValues, values);

  const getRightElement = () => {
    if (state === InputState.LOADING) {
      return <Spinner size="xs" color="gray" />;
    }

    if (state === InputState.SAVED) {
      return <CheckIcon mr={10} color="green.400" />;
    }

    return null;
  };

  const { mutateAsync: updateUser } = useUpdateUser({
    userId,
    values,
    changeSet,
    onSuccess: () => {
      showToast('Lead Saved!', 'success');

      // handle states for spinner / check mark.
      queryClient.invalidateQueries({ queryKey: ['userDetails'] });

      setTimeout(() => {
        setState(InputState.SAVED);
        setTimeout(() => {
          setState(InputState.IDLE);
        }, 500);
      }, 1000);
    },
  });

  useEffect(() => {
    if (meta.error || meta.initialValue === meta.value) return;

    if (meta.value === '') setFieldValue(name, null);

    if (autosave) {
      const timeoutId = setTimeout(async () => {
        setState(InputState.LOADING);
        if (refetchQuery) {
          /** await to prevent race condition */
          await updateUser();
          return refetchQuery();
        }
        updateUser();
      }, 750);
      return () => {
        clearTimeout(timeoutId);
      };
    }

    if (onChange) {
      onChange(handleFieldValue(field.value));
    }
  }, [field.value, meta.error]);

  useEffect(() => {
    setState(loading ? InputState.LOADING : InputState.IDLE);
  }, [loading]);

  return (
    <FormControl>
      {label ? <InputLabel label={label} /> : null}
      <InputGroup>
        <ChakraSelect
          autoComplete="off"
          bg={softRequired ? colors.softRequired : 'white'}
          borderColor="gray.200"
          borderWidth={1}
          focusBorderColor={meta.error ? 'red.600' : 'purple.500'}
          id={name}
          name={name}
          placeholder="Select"
          width="100%"
          {...field}
          fontSize={fontSize}
          {...rest}
        />
        {getRightElement() && !bypassRightElement ? <InputRightElement>{getRightElement()}</InputRightElement> : null}
      </InputGroup>
    </FormControl>
  );
}
